import React, { useState, useContext, useMemo } from 'react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  UserPlusIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from '@phosphor-icons/react';
import useWatchers from 'src/api/core/watches/useWatchers';
import useCreateWatch from 'src/api/core/watches/useCreateWatch';
import useSearchUsers from 'src/api/core/users/useSearchUsers';
import { WatcherDTO } from 'src/types/application/WatcherDTO';
import { AuthContext } from 'src/context/auth.context';
import {
  MiniAvatarList,
  type MiniAvatarListProps,
} from 'src/components/MiniAvatarList';

type WatchersProps = Omit<MiniAvatarListProps, 'users'> & {
  watchableEntityType: string;
  watchableId: string;
  onWatcherChange?: (watchers: WatcherDTO[]) => void;
};

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  avatarSizeClasses,
  AvatarWithFallback,
} from '../users/AvatarWithFallback';
import { WatcherListItem } from './WatcherListItem';

const Watchers: React.FC<WatchersProps> = ({
  watchableEntityType,
  watchableId,
  maxVisible = 3,
  size = 'md',
  className,
  onWatcherChange,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { currentUser } = useContext(AuthContext);

  const { data: watchersData, isLoading } = useWatchers({
    watchableEntityType,
    watchableId,
  });

  // Search for users when there's a search term
  const { data: searchUsersData, isLoading: isSearching } = useSearchUsers(
    { query: searchTerm, limit: 10 },
    searchTerm.trim().length > 0,
  );

  const createWatch = useCreateWatch({
    watchableEntityType,
    watchableId,
  });

  const watchers = useMemo(() => watchersData?.watchers || [], [watchersData]);
  const searchResults = searchUsersData?.users || [];

  const handleToggleWatchKind = async (watcher: WatcherDTO) => {
    if (!currentUser) return;

    const newWatchKind =
      watcher.watchKind === 'watching' ? 'assigned' : 'watching';

    try {
      // Assign/unassign this person
      await createWatch.mutateAsync({
        watchableEntityType,
        watchableId,
        watchKind: newWatchKind,
        userId: watcher.id,
      });

      onWatcherChange?.(
        watchers.map((w) =>
          w.id === watcher.id ? { ...w, watchKind: newWatchKind } : w,
        ),
      );
    } catch (error) {
      console.error('Failed to toggle watch kind:', error);
    }
  };

  const handleAddCurrentUser = async () => {
    if (!currentUser) return;

    try {
      await createWatch.mutateAsync({
        watchableEntityType,
        watchableId,
        watchKind: 'watching',
        userId: currentUser.id,
      });
      onWatcherChange?.(watchers);
    } catch (error) {
      console.error('Failed to add watcher:', error);
    }
  };

  const handleAddUserAsWatcher = async (userId: string) => {
    if (!currentUser) return;

    try {
      await createWatch.mutateAsync({
        watchableEntityType,
        watchableId,
        watchKind: 'watching',
        userId,
      });
      onWatcherChange?.(watchers);
      setSearchTerm(''); // Clear search after adding
    } catch (error) {
      console.error('Failed to add user as watcher:', error);
    }
  };
  const isCurrentUserWatcher =
    currentUser && watchers.some((w) => w.id === currentUser.id);

  // Filter out users who are already watchers from search results
  const filteredSearchResults = searchResults.filter(
    (user) => !watchers.some((watcher) => watcher.id === user.id),
  );

  const sortedWatchers = useMemo(
    () =>
      watchers.toSorted((a, b) => {
        const aIsAssigned = a.watchKind === 'assigned';
        const bIsAssigned = b.watchKind === 'assigned';
        if (aIsAssigned && !bIsAssigned) return -1;
        if (!aIsAssigned && bIsAssigned) return 1;
        return 0;
      }),
    [watchers],
  );

  return (
    <div className={cn('relative', className)}>
      <Popover>
        <PopoverTrigger>
          {sortedWatchers.length > 0 ? (
            <MiniAvatarList
              users={sortedWatchers}
              size={size}
              maxVisible={maxVisible}
              className={cn(className, 'cursor-pointer')}
            />
          ) : (
            <Tooltip>
              <TooltipTrigger>
                <Avatar
                  className={cn(
                    avatarSizeClasses[size],
                    'shadow-sm ring-2 ring-background',
                  )}
                >
                  <AvatarFallback className="text-xs font-medium bg-muted">
                    <PlusIcon />
                  </AvatarFallback>
                </Avatar>
              </TooltipTrigger>
              <TooltipContent>
                <p>Assign or Add Followers</p>
              </TooltipContent>
            </Tooltip>
          )}
        </PopoverTrigger>

        <PopoverContent className="w-80">
          <div className="pb-3 border-b">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="font-semibold text-sm">Assign & Notify</h3>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              Assign someone to handle this or add followers to get notified
            </p>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search users to add..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto">
            {/* Search Results */}
            {searchTerm.trim().length > 0 && (
              <div className="py-2">
                <div className="text-xs font-medium text-gray-700 mb-2 px-3">
                  Click to add as follower
                </div>
                {isSearching ? (
                  <div className="p-4 text-center text-sm text-gray-500">
                    Searching users...
                  </div>
                ) : filteredSearchResults.length === 0 ? (
                  <div className="p-4 text-center text-sm text-gray-500">
                    No users found
                  </div>
                ) : (
                  <div className="space-y-1">
                    {filteredSearchResults.map((user) => (
                      <Button
                        key={user.id}
                        variant="ghost"
                        className="flex items-center justify-between w-full text-left px-3 py-2 h-auto hover:bg-blue-50"
                        onClick={() => handleAddUserAsWatcher(user.id)}
                        disabled={createWatch.isLoading}
                      >
                        <div className="flex items-center gap-3">
                          <AvatarWithFallback user={user} size="sm" />
                          <div>
                            <div className="font-medium text-sm">
                              {user.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {user.email}
                            </div>
                          </div>
                        </div>
                        <UserPlusIcon className="h-4 w-4 flex-shrink-0 text-blue-600" />
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Current Watchers */}
            {searchTerm.trim().length === 0 && (
              <>
                {isLoading ? (
                  <div className="p-4 text-center text-sm text-gray-500">
                    Loading...
                  </div>
                ) : watchers.length === 0 ? (
                  <div className="p-4 text-center text-sm text-gray-500">
                    No one assigned or following yet
                  </div>
                ) : (
                  <div className="py-2 space-y-4">
                    {/* Assigned Section */}
                    <div>
                      <div className="text-xs font-semibold text-gray-700 mb-2 px-2">
                        Assigned To
                      </div>
                      {sortedWatchers.some(
                        (w) => w.watchKind === 'assigned',
                      ) ? (
                        sortedWatchers
                          .filter((w) => w.watchKind === 'assigned')
                          .map((watcher) => (
                            <WatcherListItem
                              key={watcher.id}
                              watcher={watcher}
                              watchableEntityType={watchableEntityType}
                              watchableId={watchableId}
                              handleToggleWatchKind={handleToggleWatchKind}
                              isAssignedSection={true}
                            />
                          ))
                      ) : (
                        <div className="px-2 py-3 text-xs text-gray-500 italic">
                          No one assigned yet. Click &ldquo;Assign&rdquo; on a
                          follower below.
                        </div>
                      )}
                    </div>

                    {/* Followers Section */}
                    {sortedWatchers.some((w) => w.watchKind === 'watching') && (
                      <div>
                        <div className="text-xs font-semibold text-gray-700 mb-2 px-2">
                          Followers
                        </div>
                        {sortedWatchers
                          .filter((w) => w.watchKind === 'watching')
                          .map((watcher) => (
                            <WatcherListItem
                              key={watcher.id}
                              watcher={watcher}
                              watchableEntityType={watchableEntityType}
                              watchableId={watchableId}
                              handleToggleWatchKind={handleToggleWatchKind}
                              isAssignedSection={false}
                            />
                          ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {!isCurrentUserWatcher && currentUser && (
            <div className="p-3 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddCurrentUser}
                className="w-full"
                disabled={createWatch.isLoading}
              >
                <UserPlusIcon className="h-4 w-4 mr-2" />
                Follow this item
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default Watchers;
